import { Link } from "react-router-dom"
import { Check } from "lucide-react"
import { Button } from "../widgets/button"

interface ActionButton {
    text: string
    href?: string
    onClick?: () => void
}

interface LockedProps {
    emoji: string
    title: string
    description: string
    features?: string[]
    actions?: {
        primary?: ActionButton
        secondary?: ActionButton
    }
}

export function PageLocked({ emoji, title, description, features, actions }: LockedProps) {
    const primaryAction = actions?.primary || { text: "立即开户", href: "/dropshipping/account" }
    const secondaryAction = actions?.secondary

    return (
        <div className="w-full flex-1 flex flex-col h-full items-center justify-center overflow-hidden">

            {/* Mobile View - Forced Single Screen Layout (Explicit Height) */}
            <div className="flex xl:hidden w-full h-[calc(100svh-60px)] flex-col bg-bgb overflow-hidden pt-10">
                {/* Top: Video area takes all available remaining space */}
                <div className="flex-1 min-h-0 w-full relative">
                    <div className="absolute inset-0 flex items-center justify-center py-4">
                        <div className="relative h-full w-auto aspect-[9/16] max-w-full rounded-2xl bg-b overflow-hidden mx-auto">
                            <iframe
                                className="w-full h-full border-0"
                                src="https://player.bilibili.com/player.html?isOutside=true&aid=656043789&bvid=BV1ga4y137BP&cid=1128010154&p=1&autoplay=0&muted=0&danmaku=0&high_quality=1"
                                scrolling="no"
                                frameBorder="0"
                                allow="autoplay; fullscreen"
                                allowFullScreen
                            />
                        </div>
                    </div>
                </div>

                {/* Bottom: Action area fixed at bottom */}
                <div className="shrink-0 w-full px-6 pt-6 pb-6 flex flex-col gap-16 bg-bgb">
                    <div className="text-center">
                        <h1 className="text-2xl font-bold text-b mb-3 leading-tight">{title}</h1>
                        <p className="text-sm text-b4 w-[90%] mx-auto leading-relaxed">{description}</p>
                    </div>

                    <div className="flex flex-col gap-3">
                        <Button
                            variant="primary"
                            size="lg"
                            className="w-full"
                            href={primaryAction.href}
                            onClick={primaryAction.onClick}
                        >
                            {primaryAction.text}
                        </Button>

                        <Button
                            variant="secondary"
                            size="lg"
                            className="w-full"
                            href="/dropshipping"
                        >
                            返回
                        </Button>
                    </div>
                </div>
            </div>

            {/* Desktop View */}
            <div className="hidden xl:flex items-stretch justify-between gap-16 2xl:gap-20 w-full mx-auto px-4 2xl:px-20">
                <div className="flex-1 flex flex-col justify-between py-4 max-w-xl">
                    <div>
                        <h1 className="text-4xl font-bold text-b mb-6">{title}</h1>
                        <p className="text-base text-b4 mb-14 leading-relaxed">{description}</p>

                        {features && (
                            <div className="space-y-4">
                                {features.map((feature, index) => (
                                    <div key={index} className="flex items-start gap-3">
                                        <div className="flex-shrink-0 w-4 h-4 rounded-full bg-b text-w flex items-center justify-center mt-1">
                                            <Check className="w-3 h-3" strokeWidth={3} />
                                        </div>
                                        <span className="text-base text-b">{feature}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex gap-4">
                        <Button
                            variant="primary"
                            size="lg"
                            className="px-8 rounded-full text-base font-medium shadow-sm"
                            href={primaryAction.href}
                            onClick={primaryAction.onClick}
                        >
                            {primaryAction.text}
                        </Button>
                    </div>
                </div>

                <div className="flex-shrink-0 relative">
                    {/* Decorative background blob or shadow could go here */}
                    <div className="w-[300px] rounded-2xl bg-b overflow-hidden">
                        <div className="aspect-[9/16]">
                            <iframe
                                className="w-full h-full"
                                src="https://player.bilibili.com/player.html?isOutside=true&aid=656043789&bvid=BV1ga4y137BP&cid=1128010154&p=1&autoplay=0&muted=0&danmaku=0&high_quality=1"
                                scrolling="no"
                                frameBorder="0"
                                allow="autoplay; fullscreen"
                                allowFullScreen
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
